/**
 * AuthProvider — 앱 전역 인증 상태 공급자.
 *
 * <AuthProvider>로 한 번 감싸고, 컴포넌트는 useAuth() 훅으로 소비.
 * 컨텍스트 객체와 타입은 ./auth-context.ts 에 분리 (Fast Refresh 호환).
 *
 * 12_통합관리_계정기기설계 §7 세션 정책:
 *  - 로그인: 저장 방식(공용 PC = session) → 비밀번호 → 기기 확인(3대 가득이면 Login 이 E-3 단계) → 사용자 확정
 *    기기 확인이 끝날 때까지 user 를 비워 둬서 라우트 가드가 앱을 열지 않는다(gatingRef).
 *  - 앱 열기: 60일 미사용·해제된 기기면 로그아웃(안내는 로그인 화면), 아니면 마지막 접속 갱신.
 *  - 로그인 중: 이 기기 문서 1개를 구독 → revoke_requested_at·해제면 스스로 로그아웃(§7-5),
 *    auto_login:false 면 지금 세션도 창을 닫으면 끝나게(session persistence) 바꾼다.
 *  AuthProvider 는 Toast·Router 바깥이라 안내는 localStorage(setLogoutNotice) → 로그인 화면이 보여 준다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  applyPersistence,
  login as svcLogin,
  logout as svcLogout,
  watchUser,
} from "@/services/auth.service";
import {
  completeDeviceLimit,
  prepareDeviceOnLogin,
  restoreSession,
  selfLog,
} from "@/services/device_session.service";
import { watchMyDevice } from "@/services/devices.repository";
import { MENU_GATE_KEYS } from "@/domain/menus";
import { currentDeviceInfo, revokeNoticeOf } from "@/domain/device_info";
import type { DeviceDoc } from "@/domain/session";
import type { User } from "@/domain/user";
import {
  clearSessionMarks,
  readAutoLoginOffHint,
  setLogoutNotice,
  writeAutoLoginOffHint,
} from "@/lib/sessionLocal";
import { AuthContext, type AuthContextValue, type LoginOutcome, type LogoutKind } from "./auth-context";

interface PendingLogin {
  user: User;
  devices: DeviceDoc[];
  existing: DeviceDoc | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthContextValue["user"]>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [device, setDevice] = useState<DeviceDoc | null>(null);
  const [sharedSession, setSharedSession] = useState(false);

  /** 로그인 흐름(비밀번호~기기 확인)이 진행 중 — 그동안 watchUser 결과로 user 를 열지 않는다 */
  const gatingRef = useRef(false);
  const pendingRef = useRef<PendingLogin | null>(null);
  /** 방금 로그인 흐름으로 확정한 uid — 늦게 도착한 watchUser 콜백이 앱 열기 점검을 다시 돌리지 않게 */
  const loggedInUidRef = useRef<string | null>(null);
  const userRef = useRef<User | null>(null);
  const deviceRef = useRef<DeviceDoc | null>(null);
  const signingOutRef = useRef(false);

  const finishLogin = useCallback((u: User, devId: string | null, shared: boolean) => {
    gatingRef.current = false;
    pendingRef.current = null;
    loggedInUidRef.current = u.uid;
    userRef.current = u;
    setDeviceId(devId);
    setSharedSession(shared);
    setError(null);
    setUser(u);
    setLoading(false);
  }, []);

  /** 앱이 스스로 로그아웃(안내 문구를 남기고) */
  const forceSignOut = useCallback(async (notice: Parameters<typeof setLogoutNotice>[0], log: Parameters<typeof svcLogout>[0]) => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setLogoutNotice(notice);
    try {
      await svcLogout(log);
    } finally {
      clearSessionMarks();
      signingOutRef.current = false;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const unsub = watchUser(async (u, err) => {
      if (!alive) return;
      if (gatingRef.current) {
        // 로그인 흐름이 상태를 정한다. 취소(signOut)로 null 이 오면 그대로 둠
        if (!u) setLoading(false);
        return;
      }
      if (!u) {
        userRef.current = null;
        loggedInUidRef.current = null;
        setUser(null);
        setDevice(null);
        setDeviceId(null);
        setSharedSession(false);
        setError(err ?? null);
        setLoading(false);
        return;
      }
      if (loggedInUidRef.current === u.uid) {
        // 로그인 흐름이 이미 확정한 사용자 — 최신 값만 반영
        userRef.current = u;
        setUser(u);
        setLoading(false);
        return;
      }
      // 저장된 로그인으로 앱을 연 경우
      const r = await restoreSession(u);
      if (!alive) return;
      if (r.status === "signout") {
        await forceSignOut(r.notice, r.log);
        return; // signOut → 콜백이 null 로 다시 온다
      }
      loggedInUidRef.current = u.uid;
      userRef.current = u;
      setDeviceId(r.deviceId);
      setSharedSession(r.shared);
      if (r.device) setDevice(r.device);
      setError(err ?? null);
      setUser(u);
      setLoading(false);
    });
    return () => {
      alive = false;
      unsub();
    };
  }, [forceSignOut]);

  // 이 기기 문서 구독 — 강제 로그아웃·자동 로그인 해제 반응(§7-5)
  const uid = user?.uid ?? null;
  const myLoginId = user?.loginId ?? "";
  useEffect(() => {
    if (!uid || !deviceId || sharedSession) {
      deviceRef.current = null;
      return;
    }
    let seen = false;
    let lastAuto: boolean | null = null;
    const unsub = watchMyDevice(
      uid,
      deviceId,
      (d) => {
        deviceRef.current = d;
        setDevice(d);
        if (!d) {
          // 있던 문서가 사라짐 = 관리자 삭제·정리 → 다음 로그인에서 다시 등록
          if (seen) void forceSignOut("released", null);
          return;
        }
        seen = true;
        const notice = revokeNoticeOf(d, uid);
        if (notice) {
          void forceSignOut(notice, null); // 접속 기록(forced_logout 등)은 함수가 남긴다
          return;
        }
        if (lastAuto !== d.auto_login) {
          const first = lastAuto === null;
          lastAuto = d.auto_login;
          writeAutoLoginOffHint(!d.auto_login, { uid, loginId: myLoginId });
          // 첫 값은 로그인·앱 열기 단계에서 이미 맞춰 둠 — 바뀔 때만 즉시 적용
          if (!first || !d.auto_login) void applyPersistence(d.auto_login ? "local" : "session").catch(() => {});
        }
      },
      (e) => console.warn("[device] 기기 문서 구독 실패(무시)", e),
    );
    return unsub;
  }, [uid, myLoginId, deviceId, sharedSession, forceSignOut]);

  const login = useCallback<AuthContextValue["login"]>(
    async (loginId, password, opts) => {
      gatingRef.current = true;
      loggedInUidRef.current = null;
      try {
        const info = currentDeviceInfo();
        const shared = !!opts?.sharedPc && info.kind === "pc";
        // 힌트는 입력한 아이디의 것만(같은 브라우저의 다른 사람 설정이 옮겨붙지 않게). 로그인 뒤 기기 문서 값으로 다시 맞춤
        const persistence = shared || readAutoLoginOffHint({ loginId }) ? "session" : "local";
        const u = await svcLogin(loginId, password, { persistence });
        const gate = await prepareDeviceOnLogin(u, { shared });
        if (gate.status === "limit") {
          pendingRef.current = { user: u, devices: gate.devices, existing: gate.existing };
          const out: LoginOutcome = { status: "device_limit", user: u, devices: gate.devices, thisDevice: gate.thisDevice };
          return out;
        }
        finishLogin(u, gate.deviceId, gate.shared);
        return { status: "ok", user: u };
      } catch (e) {
        gatingRef.current = false;
        pendingRef.current = null;
        throw e;
      }
    },
    [finishLogin],
  );

  const resolveDeviceLimit = useCallback<AuthContextValue["resolveDeviceLimit"]>(
    async (releaseDeviceId) => {
      const p = pendingRef.current;
      if (!p) throw new Error("로그인을 다시 시작하세요.");
      const target = p.devices.find((d) => d.id === releaseDeviceId);
      if (!target) throw new Error("해제할 기기를 선택하세요.");
      const devId = await completeDeviceLimit(p.user, target, p.existing);
      finishLogin(p.user, devId, false);
    },
    [finishLogin],
  );

  const cancelLogin = useCallback<AuthContextValue["cancelLogin"]>(async () => {
    pendingRef.current = null;
    try {
      await svcLogout(null);
    } finally {
      clearSessionMarks();
      gatingRef.current = false;
      setLoading(false);
    }
  }, []);

  const logout = useCallback<AuthContextValue["logout"]>(
    async (kind: LogoutKind = "logout") => {
      const u = userRef.current;
      const d = deviceRef.current;
      const info = currentDeviceInfo();
      const log = u
        ? selfLog(
            u,
            kind,
            sharedSession
              ? { id: "", name: "공용 PC", browser: info.browser, os: info.os, kind: info.kind }
              : deviceId
                ? { id: deviceId, name: d?.name || info.defaultName, browser: d?.browser || info.browser, os: d?.os || info.os, kind: d?.kind ?? info.kind }
                : null,
            sharedSession,
          )
        : null;
      if (kind === "idle_logout") setLogoutNotice("idle");
      try {
        await svcLogout(log);
      } finally {
        clearSessionMarks();
      }
    },
    [deviceId, sharedSession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      login,
      resolveDeviceLimit,
      cancelLogin,
      logout,
      deviceId,
      device,
      sharedSession,
      // 두 번째 인자 tenant 는 아직 쓰지 않는다(결과 동일) — 2단계에서 법인별 역할(user.tenantRoles[tenant])로 판정할 자리
      hasPerm: (key) => {
        if (!user) return false;
        if (user.isAdmin) return true;
        if (!user.active) return false;
        // 개별 오버라이드가 그룹보다 우선: deny > allow > 그룹
        const eff = user.overrides[key];
        if (eff === "deny") return false;
        if (eff === "allow") return true;
        if (user.permissions.includes(key)) return true;
        // 전체 메뉴 허용: 메뉴 게이트(.view) 키는 통과 (업무권한은 별도)
        if (user.allMenus && MENU_GATE_KEYS.has(key)) return true;
        return false;
      },
    }),
    [user, loading, error, login, resolveDeviceLimit, cancelLogin, logout, deviceId, device, sharedSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
